import { NextRequest, NextResponse } from 'next/server';
import { Resend } from 'resend';

const resend = new Resend(process.env.RESEND_API_KEY);
const ADMIN_EMAIL = process.env.ADMIN_EMAIL || 'admin@example.com';

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    const { type, domain, emails, contact, company, price } = body;

    // Type: lead | simulation | payment | callback
    let subject = '';
    let htmlContent = '';

    if (type === 'lead') {
      // Early lead capture from Step 2
      subject = `[LEAD] Nouveau prospect - ${company?.name || 'Inconnu'}`;
      htmlContent = buildLeadEmail(contact, company);
    } else if (type === 'simulation') {
      // Simulation sent to user from Step 4
      subject = `[SIMULATION] ${contact.fullName} - ${domain}`;
      htmlContent = buildSimulationAdminEmail(contact, domain, emails, price);
    } else {
      // payment or callback
      const isPayment = type === 'payment';
      subject = isPayment
        ? `[PAIEMENT] Nouvelle commande - ${domain}`
        : `[RAPPEL] Demande de contact - ${domain}`;
      htmlContent = buildOrderEmail(contact, domain, emails, isPayment);
    }

    await resend.emails.send({
      from: 'Email Pro <onboarding@resend.dev>',
      to: [ADMIN_EMAIL],
      subject,
      html: htmlContent,
    });

    return NextResponse.json({ success: true });
  } catch (error) {
    console.error('Notify admin error:', error);
    return NextResponse.json(
      { error: 'Failed to send notification' },
      { status: 500 }
    );
  }
}

function buildLeadEmail(contact: { fullName: string; email: string; phone: string }, company: { name: string; sector?: string }) {
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #f59e0b; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 20px;">🔔 Nouveau Prospect</h1>
      </div>
      <div style="padding: 24px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
        <p style="color: #64748b; margin-top: 0;">Un nouveau prospect a commencé la simulation.</p>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #64748b; width: 140px;">Nom</td>
            <td style="padding: 8px 0; font-weight: 600;">${contact.fullName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Email</td>
            <td style="padding: 8px 0;">${contact.email}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Téléphone</td>
            <td style="padding: 8px 0;">${contact.phone}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Entreprise</td>
            <td style="padding: 8px 0;">${company.name}${company.sector ? ` (${company.sector})` : ''}</td>
          </tr>
        </table>
        <div style="background: #fef3c7; padding: 16px; border-radius: 8px; margin-top: 16px;">
          <p style="margin: 0; color: #92400e; font-weight: 600;">Prospect en cours de simulation</p>
          <p style="margin: 8px 0 0; color: #a16207;">Si le prospect abandonne, vous pourrez le relancer au ${contact.phone}</p>
        </div>
      </div>
    </div>
  `;
}

function buildSimulationAdminEmail(contact: { fullName: string; email: string; phone: string }, domain: string, emails: string[], price: number) {
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #8b5cf6; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 20px;">📧 Simulation envoyée au prospect</h1>
      </div>
      <div style="padding: 24px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
        <p style="color: #64748b; margin-top: 0;">Le prospect a demandé à recevoir sa simulation par email. Il est intéressé mais hésite peut-être sur le prix.</p>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #64748b; width: 140px;">Nom</td>
            <td style="padding: 8px 0; font-weight: 600;">${contact.fullName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Email</td>
            <td style="padding: 8px 0;">${contact.email}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Téléphone</td>
            <td style="padding: 8px 0;">${contact.phone}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Domaine</td>
            <td style="padding: 8px 0; font-weight: 600; color: #2563eb;">${domain}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Emails</td>
            <td style="padding: 8px 0;">${emails.map((e: string) => `<div>${e}@${domain}</div>`).join('')}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Prix affiché</td>
            <td style="padding: 8px 0; font-weight: 600;">${price?.toLocaleString('fr-FR') || '—'} XOF / an</td>
          </tr>
        </table>
        <div style="background: #ede9fe; padding: 16px; border-radius: 8px; margin-top: 16px;">
          <p style="margin: 0; color: #6d28d9; font-weight: 600;">Prospect chaud - à relancer</p>
          <p style="margin: 8px 0 0; color: #7c3aed;">Contacter le prospect au ${contact.phone} pour finaliser la vente.</p>
        </div>
      </div>
    </div>
  `;
}

function buildOrderEmail(contact: { fullName: string; email: string; phone: string; country: string }, domain: string, emails: string[], isPayment: boolean) {
  return `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: ${isPayment ? '#16a34a' : '#2563eb'}; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h1 style="margin: 0; font-size: 20px;">
          ${isPayment ? '💰 Nouveau Paiement Reçu' : '📞 Demande de Rappel'}
        </h1>
      </div>
      <div style="padding: 24px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
        <h2 style="color: #0f172a; margin-top: 0;">Informations client</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #64748b; width: 140px;">Nom complet</td>
            <td style="padding: 8px 0; font-weight: 600;">${contact.fullName}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Email</td>
            <td style="padding: 8px 0;">${contact.email}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Téléphone</td>
            <td style="padding: 8px 0;">${contact.phone}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Pays</td>
            <td style="padding: 8px 0;">${contact.country}</td>
          </tr>
        </table>
        <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 16px 0;" />
        <h2 style="color: #0f172a;">Commande</h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; color: #64748b; width: 140px;">Domaine</td>
            <td style="padding: 8px 0; font-weight: 600; color: #2563eb;">${domain}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b; vertical-align: top;">Emails</td>
            <td style="padding: 8px 0;">${emails.map((e: string) => `<div>${e}@${domain}</div>`).join('')}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; color: #64748b;">Statut</td>
            <td style="padding: 8px 0; font-weight: 600; color: ${isPayment ? '#16a34a' : '#2563eb'};">
              ${isPayment ? 'PAYÉ' : 'EN ATTENTE DE RAPPEL'}
            </td>
          </tr>
        </table>
        ${
          !isPayment
            ? `<div style="background: #dbeafe; padding: 16px; border-radius: 8px; margin-top: 16px;">
                <p style="margin: 0; color: #1d4ed8; font-weight: 600;">Action requise</p>
                <p style="margin: 8px 0 0; color: #1e40af;">Rappeler le client au ${contact.phone}</p>
              </div>`
            : `<div style="background: #dcfce7; padding: 16px; border-radius: 8px; margin-top: 16px;">
                <p style="margin: 0; color: #16a34a; font-weight: 600;">Action requise</p>
                <p style="margin: 8px 0 0; color: #15803d;">Procéder à la configuration du domaine et des emails.</p>
              </div>`
        }
      </div>
    </div>
  `;
}